import { Link, useSearchParams } from 'react-router';
import type { CarePlanListItem, CarePlanStatus } from '@mys/contracts';
import { ApiError } from '../lib/api.js';
import {
  CARE_PLAN_STATUSES,
  CARE_PLAN_STATUS_LABELS,
  useCarePlans,
  useUpdateCarePlan,
} from '../lib/billing.js';
import { useCan } from '../lib/permissions.js';
import { formatDate, formatInr } from '../lib/format.js';

/* ============================================================================
   /care-plans — the recurring half of the business.
   ----------------------------------------------------------------------------
   Readable by any admin. Pausing, cancelling and resuming are owner-only: the
   API answers 403, and the buttons are hidden for staff rather than shown and
   then refused.
   ========================================================================== */

function PlanActions({ plan }: { plan: CarePlanListItem }) {
  const update = useUpdateCarePlan(plan.id);

  /* What each state can become. A cancelled plan can be resumed — that is how
     someone resubscribes — and the API gives it a fresh billing date so it
     does not charge them the moment it comes back. */
  const actions: { to: CarePlanStatus; label: string }[] =
    plan.status === 'active'
      ? [
          { to: 'paused', label: 'Pause' },
          { to: 'cancelled', label: 'Cancel' },
        ]
      : plan.status === 'paused'
        ? [
            { to: 'active', label: 'Resume' },
            { to: 'cancelled', label: 'Cancel' },
          ]
        : [{ to: 'active', label: 'Resume' }];

  return (
    <div className="row" style={{ marginTop: 8, gap: 6 }}>
      {actions.map((action) => (
        <button
          key={action.to}
          type="button"
          className="btn btn--ghost btn--small"
          disabled={update.isPending}
          onClick={() => update.mutate({ status: action.to })}
        >
          {action.label}
        </button>
      ))}
      {update.isError ? (
        <span className="field__error" role="alert">
          {update.error instanceof ApiError ? update.error.message : 'Could not save.'}
        </span>
      ) : null}
    </div>
  );
}

export function CarePlansPage() {
  const [params, setParams] = useSearchParams();
  const may = useCan();
  const mayEdit = may('care-plans:edit');

  const statusParam = params.get('status');
  const status = CARE_PLAN_STATUSES.includes(statusParam as CarePlanStatus)
    ? (statusParam as CarePlanStatus)
    : undefined;

  const { data, isPending, error } = useCarePlans(status ? { status } : {});

  function setStatus(value: CarePlanStatus | undefined) {
    const next = new URLSearchParams(params);
    if (value) next.set('status', value);
    else next.delete('status');
    setParams(next, { replace: true });
  }

  const items = data?.items ?? [];
  const counts = data?.counts ?? {};

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Care plans</h1>
          <p className="muted">
            {data ? `${data.total} plan` : 'Loading…'}
            {/* Active plans only. A paused plan is not revenue. */}
            {data ? ` · ${formatInr(data.mrrInr)} a month` : ''}
          </p>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={status ? 'btn btn--ghost btn--small' : 'btn btn--small'}
          onClick={() => setStatus(undefined)}
        >
          All
        </button>
        {CARE_PLAN_STATUSES.map((value) => (
          <button
            key={value}
            type="button"
            className={status === value ? 'btn btn--small' : 'btn btn--ghost btn--small'}
            onClick={() => setStatus(value)}
          >
            {CARE_PLAN_STATUS_LABELS[value]} {counts[value] ?? 0}
          </button>
        ))}
      </div>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load care plans.'}
        </p>
      ) : null}

      {isPending ? <p className="muted">Loading…</p> : null}

      {data && items.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }}>
            {status ? 'Nothing matches this filter.' : 'No care plans yet.'}
          </p>
          {!status ? (
            <>
              <p className="muted" style={{ margin: '6px 0 0' }}>
                A care plan is created from inside a client.
              </p>
              <Link to="/clients" className="btn btn--small" style={{ marginTop: 12 }}>
                Clients kholiye
              </Link>
            </>
          ) : null}
        </div>
      ) : null}

      <div className="stack">
        {items.map((plan) => (
          <article className="card" key={plan.id}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div>
                <Link to={`/clients/${plan.clientId}`} className="lead-card__business">
                  {plan.business}
                </Link>
                <div className="lead-card__meta">{formatInr(plan.monthlyInr)}/month</div>
              </div>
              <span className={plan.status === 'active' ? 'pill' : 'pill pill--muted'}>
                {CARE_PLAN_STATUS_LABELS[plan.status]}
              </span>
            </div>

            <div className="lead-card__meta" style={{ marginTop: 8 }}>
              {plan.status === 'cancelled' && plan.cancelledAt
                ? `Cancelled ${formatDate(plan.cancelledAt)}`
                : plan.nextBillingAt
                  ? `Next billing ${formatDate(plan.nextBillingAt)}`
                  : 'No billing date'}
            </div>

            {/* Hidden for staff rather than shown and then refused with a 403.
                The API is still the gate. */}
            {mayEdit ? <PlanActions plan={plan} /> : null}
          </article>
        ))}
      </div>
    </>
  );
}
